import { useContext } from 'react';
import { createPortal } from 'react-dom';
import { observer } from 'mobx-react-lite';
import { AppContext } from 'core/context/app.context';
import { RewardCard } from 'core/models/rewards/rewards';
import { RewardsStore } from 'rewards/rewards.store';
import BrandMissMatch from 'welcome-screen/brand-miss-match/brand-miss-match';
import WelcomePopup from 'welcome-screen/welcome-popup/welcome-popup';
import { CampaignPopupStore } from './campaign-popup.store';
import { AccountDetailsPopup } from './layouts/account-details';
import { CampaignPopup } from './layouts/campaign-popup';
import { DateSelectorPopup } from './layouts/date-selector';
import ErrorNotice from './layouts/error-notice/error-notice';
import { FixErrorProcessing } from './layouts/fix-error-proccesing';
import { PaymentNoticePopup } from './layouts/payment-notice';
import { PaymentReminderPopUp } from './layouts/payment-reminder';
import { PremiumExperiencePopUp } from './layouts/premium-experience-pop-up';
import { ProfileImageUpdater } from './layouts/profile-image-updater';
import { ReconnectUpsizePopup } from './layouts/reconnect-upsize';
import UnlockSimInfo from './layouts/unlock-sim-info/unlock-sim-info';
import { PopupLayout, PopupStore } from './popup.store';
import styles from './popup.module.scss';

type Lang = {
  heading?: string;
  subHeading?: string;
};

const modalElement = document.getElementById('modal-root') ?? document.body.appendChild(document.createElement('div'));
modalElement.id = 'modal-root';

export const Popup = observer(
  ({
    popupStore,
    lang,
    children,
    rewardCards,
    rewardStore,
    genericPopupStore,
    customAction,
  }: {
    popupStore: PopupStore;
    lang?: Lang;
    children?: JSX.Element;
    rewardCards?: RewardCard[];
    rewardStore?: RewardsStore;
    genericPopupStore?: CampaignPopupStore;
    customAction?: () => void;
  }) => {
    const appContext = useContext(AppContext);
    const isDark =
      popupStore?.config?.template === PopupLayout.UnlockSimInfo ||
      popupStore?.config?.template === PopupLayout.WelcomePopup;

    const lightOverlayTemplates = [
      PopupLayout.Error,
      PopupLayout.UnlockSimInfo,
      PopupLayout.WelcomePopup,
      PopupLayout.PaymentNotice,
      PopupLayout.PaymentReminder,
      PopupLayout.BrandMissMatch,
      PopupLayout.InAppGeneric,
    ];

    const isLightOverlay = lightOverlayTemplates.includes(popupStore?.config?.template);

    document.addEventListener('keyup', (ev) => {
      if (ev.code === 'Escape' || ev.key === 'Escape') {
        popupStore.defaultClose();
      }
    });

    if (popupStore.config === undefined || !popupStore.isOpen) {
      return;
    }
    const largeLayoutTemplates = [PopupLayout.InAppGeneric, PopupLayout.ReconnectUpsize];

    const isPremiumExperiencesItem = popupStore?.config?.template === PopupLayout.PremiumExperiences;
    const isWelcomePopupTemplate = popupStore?.config?.template === PopupLayout.WelcomePopup;
    const isLargeTemplate = largeLayoutTemplates.includes(popupStore?.config?.template);

    const useDarkContainer = isDark ? styles.popupContainerDark : styles.popupContainer;
    const useScreenWideContainer = isPremiumExperiencesItem ? ` ${styles.wide}` : '';
    const useLargeContainer = isLargeTemplate ? ` ${styles.large}` : '';
    const isWelcomePopup = isWelcomePopupTemplate ? ` ${styles.welcomePopupContainer}` : '';

    const popupContainerCss = useDarkContainer + useScreenWideContainer + isWelcomePopup + useLargeContainer;

    return createPortal(
      <div>
        <div className={styles.popupOuter}>
          <div
            className={isLightOverlay ? styles.overlayLight : styles.overlay}
            onClick={(e) => {
              popupStore.config.allowBackgroundClose && popupStore.defaultClose();
            }}
          ></div>
          <div className={popupContainerCss}>
            {popupStore.config.hideCloseIcon ? null : (
              <div className={styles.popupHeadingContainer}>
                <h3 className={styles.popupHeading}>{lang?.heading}</h3>
                <i className={`text-dark icon-close ${styles.closeIcon}`} onClick={() => popupStore.defaultClose()}></i>
              </div>
            )}
            {popupStore.config?.subHeading && (
              <p className={`text-regular ${styles.popupSubHeading}`}>{lang?.subHeading}</p>
            )}
            <div className={styles.contentContainer}>
              <div id="popupContent">
                {
                  {
                    [PopupLayout.DateSelector]: (
                      <DateSelectorPopup popupStore={popupStore} styles={styles}></DateSelectorPopup>
                    ),
                    [PopupLayout.PaymentNotice]: (
                      <PaymentNoticePopup popupStore={popupStore} styles={styles}></PaymentNoticePopup>
                    ),
                    [PopupLayout.PaymentReminder]: (
                      <PaymentReminderPopUp popupStore={popupStore}></PaymentReminderPopUp>
                    ),
                    [PopupLayout.AccountDetail]: (
                      <AccountDetailsPopup
                        popupStore={popupStore}
                        styles={styles}
                        customerNumber={appContext.customerNumber}
                        country={appContext.countryName}
                        accountNumber={appContext.accountNumber}
                      ></AccountDetailsPopup>
                    ),
                    [PopupLayout.ProfileImageUploader]: (
                      <ProfileImageUpdater popupStore={popupStore} styles={styles}></ProfileImageUpdater>
                    ),
                    [PopupLayout.FixErrorProcessing]: <FixErrorProcessing popupStore={popupStore} />,
                    [PopupLayout.UnlockSimInfo]: <UnlockSimInfo popupStore={popupStore}></UnlockSimInfo>,
                    [PopupLayout.WelcomePopup]: <WelcomePopup popupStore={popupStore}></WelcomePopup>,
                    [PopupLayout.BrandMissMatch]: <BrandMissMatch popupStore={popupStore}></BrandMissMatch>,
                    [PopupLayout.Error]: <ErrorNotice popupStore={popupStore}></ErrorNotice>,
                    [PopupLayout.PremiumExperiences]: rewardStore && (
                      <PremiumExperiencePopUp
                        popupStore={popupStore}
                        rewardsStore={rewardStore}
                        rewardCards={rewardCards}
                        onClaim={customAction}
                      ></PremiumExperiencePopUp>
                    ),
                    [PopupLayout.InAppGeneric]: genericPopupStore && (
                      <CampaignPopup popupStore={popupStore} store={genericPopupStore}></CampaignPopup>
                    ),
                    [PopupLayout.ReconnectUpsize]: <ReconnectUpsizePopup onReconnect={customAction} />,
                  }[popupStore.config.template]
                }
              </div>
            </div>
          </div>
        </div>
      </div>,
      modalElement
    );
  }
);